import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faKeyboard } from '@fortawesome/free-solid-svg-icons';
import './SearchBar.css';

function SearchBar({ onSearchChange, onSearch, query: queryProp, onKeyboardToggle }) {
  const [query, setQuery] = useState(queryProp || '');
  const [errorMessage, setErrorMessage] = useState('');
  const [showKeyboard, setShowKeyboard] = useState(false);
  const queryRef = useRef(query);

  useEffect(() => {
    if (queryProp !== undefined) {
      setQuery(queryProp);
    }
  }, [queryProp]);

  // Mapping from keyboard keys to Gurmukhi characters
  // Each key maps to exactly one Gurmukhi character - no duplicates
  const gurmukhiKeyMap = {
    // Vowels
    'a': 'ਅ', 'A': 'ਆ',
    'i': 'ਇ', 'I': 'ਈ',
    'u': 'ਉ', 'U': 'ਊ',
    'e': 'ਏ', 'E': 'ਐ',
    'q': 'ਓ', 'O': 'ਔ',
    
    // Row 1: ੳ, ਅ, ੲ, ਸ, ਹ, ਕ, ਖ, ਗ, ਘ, ਙ
    'o': 'ੳ', 'Q': 'ਙ',
    'w': 'ੲ', 'W': 'ਞ',
    's': 'ਸ', 'S': 'ਸ਼',
    'h': 'ਹ', 'N': 'ਣ',
    'k': 'ਕ', 'K': 'ਖ',
    'g': 'ਗ', 'G': 'ਗ਼',
    
    // Row 2: ਚ, ਛ, ਜ, ਝ, ਞ, ਟ, ਠ, ਡ, ਢ, ਣ
    'c': 'ਚ', 'C': 'ਛ',
    'j': 'ਜ', 'J': 'ਝ',
    't': 'ਤ', 'T': 'ਠ',
    'd': 'ਦ', 'D': 'ਡ',
    '[': 'ਟ', '{': 'ਢ',
    
    // Row 3: ਤ, ਥ, ਦ, ਧ, ਨ, ਪ, ਫ, ਬ, ਭ, ਮ
    'p': 'ਪ', 'P': 'ਫ',
    'b': 'ਬ', 'B': 'ਭ',
    'm': 'ਮ', 'n': 'ਨ',
    ']': 'ਥ', '}': 'ਧ',
    
    // Row 4: ਯ, ਰ, ਲ, ਵ, ੜ, ਸ਼, ਖ਼, ਗ਼, ਜ਼, ਫ਼
    'y': 'ਯ', 'H': 'ਘ',
    'r': 'ਰ', 'R': 'ੜ',
    'l': 'ਲ',
    'v': 'ਵ',
    'f': 'ਫ', 'F': 'ਫ਼',
    'z': 'ਜ਼',
    'x': '੍',
    ':': 'ਖ਼',
    
    // Numbers
    '0': '0', '1': '1', '2': '2', '3': '3', '4': '4',
    '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
    
    // Matras (vowel signs)
    '^': 'ਾ',  // aa (kanna)
    '&': 'ਿ',  // i (sihari)
    '*': 'ੀ',  // ee (bihari)
    '(': 'ੁ',  // u (aunkar)
    ')': 'ੂ',  // oo (dulankar)
    '-': 'ੇ',  // e (lawan)
    '_': 'ੈ',  // ai (dulawan)
    '+': 'ੋ',  // o (hora)
    '=': 'ੌ',  // au (kanaura)
    
    // Additional diacritics
    '|': 'ੰ',  // tippi
    '\\': 'ੱ', // addhak
    ';': 'ਂ',  // bindi
  };

  const gurmukhiKeys = [
    ['ੳ', 'ਅ', 'ੲ', 'ਸ', 'ਹ', 'ਕ', 'ਖ', 'ਗ', 'ਘ', 'ਙ'],
    ['ਚ', 'ਛ', 'ਜ', 'ਝ', 'ਞ', 'ਟ', 'ਠ', 'ਡ', 'ਢ', 'ਣ'],
    ['ਤ', 'ਥ', 'ਦ', 'ਧ', 'ਨ', 'ਪ', 'ਫ', 'ਬ', 'ਭ', 'ਮ'],
    ['ਯ', 'ਰ', 'ਲ', 'ਵ', 'ੜ', 'ਸ਼', 'ਖ਼', 'ਗ਼', 'ਜ਼', 'ਫ਼'],
    ['ਅ', 'ਆ', 'ਇ', 'ਈ', 'ਉ', 'ਊ', 'ਏ', 'ਐ', 'ਓ', 'ਔ'],
    ['ਾ', 'ਿ', 'ੀ', 'ੁ', 'ੂ', 'ੇ', 'ੈ', 'ੋ', 'ੌ'],
    ['ੰ', 'ੱ', 'ਂ', '੍']
  ];

  // Create reverse mapping (Gurmukhi to Roman key)
  // Each Gurmukhi character maps to the single key that produces it
  const reverseKeyMap = {};
  Object.keys(gurmukhiKeyMap).forEach(key => {
    const gurmukhiChar = gurmukhiKeyMap[key];
    // Only map if not already mapped (first occurrence wins for duplicates)
    if (!reverseKeyMap[gurmukhiChar]) {
      reverseKeyMap[gurmukhiChar] = key;
    }
  });

  // Helper function to get Roman key for a Gurmukhi character
  const getRomanKey = (gurmukhiChar) => {
    return reverseKeyMap[gurmukhiChar] || '';
  };

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // Convert Roman/English characters to Gurmukhi
  const convertToGurmukhi = (text) => {
    let converted = '';
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      // Check if character has a Gurmukhi mapping
      if (gurmukhiKeyMap.hasOwnProperty(char)) {
        converted += gurmukhiKeyMap[char];
      } else {
        // Keep the character as is (for numbers, spaces, punctuation, etc.)
        converted += char;
      }
    }
    return converted;
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Convert Roman characters to Gurmukhi in real-time
    const convertedValue = convertToGurmukhi(inputValue);
    setQuery(convertedValue);
    queryRef.current = convertedValue;
    if (onSearchChange) onSearchChange(convertedValue);
    if (convertedValue.trim() === '') setErrorMessage('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Close keyboard if open
    if (showKeyboard) {
      setShowKeyboard(false);
      onKeyboardToggle(false);
    }
    const currentQuery = queryRef.current || query;
    if (!currentQuery.trim()) {
      setErrorMessage('Please enter a page or verse');
      return;
    }
    setErrorMessage('');
    if (onSearch) {
      onSearch();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Close keyboard if open
      if (showKeyboard) {
        setShowKeyboard(false);
        onKeyboardToggle(false);
      }
      const currentQuery = queryRef.current || query;
      if (!currentQuery.trim()) {
        setErrorMessage('Please enter a page or verse');
        return;
      }
      setErrorMessage('');
      if (onSearch) {
        onSearch();
      }
    }
  };

  const handleKeyClick = (key) => {
    setQuery(prevQuery => {
      const newQuery = prevQuery + key;
      queryRef.current = newQuery;
      if (onSearchChange) {
        onSearchChange(newQuery);
      }
      return newQuery;
    });
  };

  const toggleKeyboard = () => {
    setShowKeyboard(!showKeyboard);
    onKeyboardToggle(!showKeyboard);
  };

  const handleBackspace = () => {
    setQuery(prevQuery => {
      const newQuery = prevQuery.slice(0, -1);
      queryRef.current = newQuery;
      if (onSearchChange) {
        onSearchChange(newQuery);
      }
      return newQuery;
    });
  };

  const handleEkOnkar = () => {
    handleKeyClick('ੴ');
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.gurmukhi-keyboard') && !event.target.closest('.search-bar')) {
        setShowKeyboard(false);
        onKeyboardToggle(false);
      }
    };

    if (showKeyboard) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showKeyboard, onKeyboardToggle]);

  return (
    <div className={`search-container ${showKeyboard ? 'keyboard-active' : ''}`}>
      <form className="search-bar" onSubmit={handleSubmit}>
        <div className="search-input-group">
          <div className="input-wrapper">
            <input
              type="text"
              placeholder="Search by page or verse"
              value={query}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="search-actions">
            <button
              type="button"
              className="keyboard-toggle"
              onClick={toggleKeyboard}
              title="Keyboard"
            >
              <FontAwesomeIcon icon={faKeyboard} />
            </button>
            <button
              type="submit"
              className="search-button"
              title="Search"
            >
              <span className="search-button-icon">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <span className="search-button-text">Search</span>
            </button>
          </div>
        </div>
        {errorMessage && <p className="error-message">{errorMessage}</p>}
      </form>

      {showKeyboard && (
        <div className="gurmukhi-keyboard" style={{ position: 'absolute', width: '100%', marginBottom: "10px" }}>
          {gurmukhiKeys.map((row, rowIndex) => (
            <div key={rowIndex} className="keyboard-row">
              {rowIndex === 0 && (
                <button 
                  className="keyboard-key" 
                  onClick={handleEkOnkar}
                  style={{ 
                    backgroundColor: '#fff3cd',
                    borderColor: '#ffc107',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  title="Ek Onkar (ੴ)"
                >
                  ੴ
                </button>
              )}
              {row.map((key, keyIndex) => {
                const romanKey = getRomanKey(key);
                const isBindi = key === 'ਂ';
                return (
                  <button 
                    key={keyIndex} 
                    className="keyboard-key" 
                    onClick={() => handleKeyClick(key)}
                    title={romanKey ? `Press '${romanKey}' on your keyboard` : ''}
                  >
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%'
                    }}>
                      {isBindi ? (
                        <span style={{ 
                          fontSize: '16px', 
                          lineHeight: '1',
                          fontWeight: '500',
                          display: 'inline-block',
                          verticalAlign: 'middle',
                          position: 'relative',
                          top: '-2px'
                        }}>ਂ</span>
                      ) : (
                        <span style={{ 
                          fontSize: '14px', 
                          lineHeight: '1.2',
                          fontWeight: '500'
                        }}>{key}</span>
                      )}
                      {romanKey && (
                        <span style={{ 
                          fontSize: '8px', 
                          color: '#6c757d', 
                          marginTop: '1px', 
                          lineHeight: '1',
                          fontFamily: 'monospace',
                          fontWeight: '400'
                        }}>
                          {romanKey}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
              {rowIndex === 0 && (
                <button 
                  className="keyboard-key" 
                  onClick={handleBackspace}
                  style={{ 
                    backgroundColor: '#dc3545',
                    color: 'white',
                    borderColor: '#c82333',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}
                  title="Backspace"
                >
                  ⌫
                </button>
              )}
            </div>
          ))}
          <button className="keyboard-close" onClick={toggleKeyboard}>Close Keyboard</button>
        </div>
      )}
    </div>
  );
}

export default SearchBar;
